/**
 * EditorStepPanel — Right panel showing property forms for the selected step.
 * Dynamically builds controls based on step type.
 */
import Phaser from 'phaser';
import { CANVAS_H, TOOLBAR_H, TIMELINE_H, STEP_PANEL_W, CANVAS_W, EditorEvents } from './EditorTypes';
import type { EditorState } from './EditorTypes';
import { TECH, fillTechRect, drawTechRect, createSmallTechButton, createTechSlider, showTextInputDialog, showTextAreaDialog, showSelectionPopup } from '@/ui/TechUI';
import { EventBus } from '@/events/EventBus';
import { t, addTranslations, getTranslation, getLanguage } from '@/i18n/I18n';
import { EnemyType, EnemyGrade, MAP_COLS, MAP_ROWS } from '@/config/GameConfig';
import { BUBBLE_TEMPLATES } from '../BubbleTemplates';
import type { BubbleTemplate, CinematicSequence } from '../CinematicTypes';
import { ConditionRegistry } from '../ConditionRegistry';

/** Collect custom (editor-authored) translations from dialogue steps in a sequence. */
export function collectCustomTranslations(seq: CinematicSequence): Record<string, string> {
  const result: Record<string, string> = {};
  for (const step of seq.steps) {
    if (step.type !== 'dialogue') continue;
    const key = (step as any).textKey as string;
    if (!key) continue;
    // Only collect if the key has a runtime translation (was set via addTranslations)
    const resolved = getTranslation(key);
    if (resolved !== undefined && resolved !== key) {
      result[key] = resolved;
    }
  }
  return result;
}

// ── Color presets for BD bubble customization ──
const BORDER_COLOR_PRESETS: { label: string; value: number }[] = [
  { label: 'Red',    value: 0xff4444 }, { label: 'Yellow', value: 0xffcc00 },
  { label: 'Green',  value: 0x44ff88 }, { label: 'Blue',   value: 0x4488ff },
  { label: 'Cyan',   value: 0x44ccff }, { label: 'Orange', value: 0xff8844 },
  { label: 'Purple', value: 0xcc44ff }, { label: 'White',  value: 0xffffff },
];
const FILL_COLOR_PRESETS: { label: string; value: number }[] = [
  { label: 'Default',    value: 0x0d0d1a }, { label: 'Dark Blue',  value: 0x111122 },
  { label: 'Navy',       value: 0x1a1a2e }, { label: 'Indigo',     value: 0x1e1e3a },
  { label: 'Dark Red',   value: 0x2a1a1a }, { label: 'Dark Green', value: 0x1a2a1a },
  { label: 'Black',      value: 0x0a0a0a }, { label: 'Slate',      value: 0x222244 },
];
const TEXT_COLOR_PRESETS: { label: string; value: number }[] = [
  { label: 'White',  value: 0xffffff }, { label: 'Gray',   value: 0xcccccc },
  { label: 'Yellow', value: 0xffcc00 }, { label: 'Cyan',   value: 0x44ccff },
  { label: 'Green',  value: 0x88ff88 }, { label: 'Pink',   value: 0xff88cc },
];

function hexStr(n: number): string {
  return '#' + n.toString(16).padStart(6, '0');
}

const PX = CANVAS_W - STEP_PANEL_W;
const PY = TOOLBAR_H;
const PH = CANVAS_H - TOOLBAR_H - TIMELINE_H;
const PAD = 10;
const ROW_H = 26;
const LABEL_W = 90;

/** Tooltip descriptions for each parameter — shown on hover over (?) icon */
const PARAM_TIPS: Record<string, string> = {
  // Common
  delayBefore:  'Wait (ms) BEFORE this step starts',
  delayAfter:   'Wait (ms) AFTER this step completes',
  // Dialogue
  speaker:      'Character shown in bubble header (select from list)',
  text:         'Click to edit the dialogue text',
  key:          'Translation key (auto-generated, editable for devs)',
  position:     'Bubble position: top / center / bottom',
  emotion:      'Bubble style: calm / alert / excited',
  autoDismiss:  'Auto-close bubble after N ms',
  worldAnchor:  'World coords for bubble tail arrow',
  portrait:     'Character portrait texture key',
  bubbleTemplate: 'Bubble shape: speech / thought / shout / whisper',
  borderColor:  'Custom border color (overrides emotion)',
  fillColor:    'Custom bubble background color',
  textColor:    'Custom text color',
  bubblePos:    'Custom screen position (drag in preview)',
  tailTip:      'Tail tip position (drag in preview)',
  // Camera Pan
  target:       'Where the camera moves to',
  coords:       'World X, Y coordinates',
  zoom:         'Camera zoom level (1 = normal)',
  duration:     'How long the camera pan takes (ms)',
  // Fake Click
  holdMs:       'How long the fake cursor stays down (ms)',
  // Action
  action:       'Which action to perform',
  count:        'Number of enemies to spawn',
  type:         'Enemy type (grunt, runner, etc.)',
  grade:        'Enemy difficulty grade',
  pathIndex:    'Which path enemies follow (0-based)',
  col:          'Grid column for robot placement',
  row:          'Grid row for robot placement',
  preset:       'Robot preset name (empty = default)',
  dispatch:     'Dispatch a real pointer event at target (not just visual)',
  // Wait
  waitEvent:    'EventBus event to wait for (optional)',
  timeoutMs:    'Max wait time before auto-advance (ms)',
  // Recap
  titleKey:     'i18n key for recap title',
  confirmKey:   'i18n key for confirm button text',
  // Spotlight
  dimAlpha:     'Dim overlay opacity (0 = transparent, 1 = black)',
  highlightColor: 'Color of the highlight border around target',
  pulse:        'Pulsing animation on highlight border',
  labelKey:     'i18n key for label text near highlight',
  labelPosition: 'Label placement relative to highlight',
  advanceOn:    'How the step advances (click on highlight or wait for event)',
  restrictInput: 'Block clicks outside the highlighted area',
  screenSpace:  'Target is in screen coords (UI) vs world coords',
  arrowDir:     'Arrow direction pointing from label toward highlight',
  skipIf:       'Condition name — skip step if evaluates to true',
};

export class EditorStepPanel {
  private scene: Phaser.Scene;
  private state: EditorState;
  private container: Phaser.GameObjects.Container;
  private contentContainer: Phaser.GameObjects.Container;
  private scrollY = 0;
  /** Floating tooltip (shared, repositioned on hover) */
  private tooltip: Phaser.GameObjects.Text | null = null;

  constructor(scene: Phaser.Scene, state: EditorState) {
    this.scene = scene;
    this.state = state;
    this.container = scene.add.container(PX, PY).setDepth(10);

    // Background
    const bg = scene.add.graphics();
    fillTechRect(bg, 0, 0, STEP_PANEL_W, PH, 0x0e0e1e, 0.95);
    drawTechRect(bg, 0, 0, STEP_PANEL_W, PH, TECH.DIM, 0.4, 1);
    this.container.add(bg);

    // Title
    const title = scene.add.text(PAD, 8, 'STEP PROPERTIES', {
      fontSize: '11px', color: '#666688', fontFamily: 'monospace',
    });
    this.container.add(title);

    // Scrollable content
    this.contentContainer = scene.add.container(0, 28);
    this.container.add(this.contentContainer);

    // Scroll mask
    const mask = scene.make.graphics({});
    mask.fillRect(PX, PY + 26, STEP_PANEL_W, PH - 26);
    this.contentContainer.setMask(new Phaser.Display.Masks.GeometryMask(scene, mask));

    // Mouse wheel scroll
    scene.input.on('wheel', (_p: Phaser.Input.Pointer, _gx: any, _dx: number, dy: number) => {
      const ptr = scene.input.activePointer;
      if (ptr.x < PX || ptr.y < PY || ptr.y > PY + PH) return;
      this.scrollY = Phaser.Math.Clamp(this.scrollY + dy * 0.4, 0, Math.max(0, this.contentHeight - PH + 40));
      this.contentContainer.y = 28 - this.scrollY;
    });

    this.refresh();
  }

  private contentHeight = 0;

  /** Rebuild the panel content for the currently selected step */
  refresh(): void {
    const prevScroll = this.scrollY;
    this.contentContainer.removeAll(true);

    const idx = this.state.selectedStepIndex;
    if (idx < 0 || idx >= this.state.sequence.steps.length) {
      this.addLabel(PAD, 20, 'Select a step', '#555566');
      this.contentHeight = 50;
      return;
    }

    const step = this.state.sequence.steps[idx];
    let y = 0;

    // Type display
    y = this.addLabel(PAD, y, `Type: ${step.type}`, '#ffcc00');
    y = this.addLabel(PAD, y, `ID: ${step.id}`, '#555566');
    y += 8;

    // Per-type fields
    switch (step.type) {
      case 'dialogue':   y = this.buildDialogue(step, y); break;
      case 'cameraPan':  y = this.buildCameraPan(step, y); break;
      case 'fakeClick':  y = this.buildFakeClick(step, y); break;
      case 'action':     y = this.buildAction(step, y); break;
      case 'wait':       y = this.buildWait(step, y); break;
      case 'recap':      y = this.buildRecap(step, y); break;
      case 'spotlight':  y = this.buildSpotlight(step, y); break;
    }

    // Common fields
    y += 8;
    y = this.addSliderRow('delayBefore', 0, 5000, 100, step.delayBefore ?? 0, y, (v) => {
      step.delayBefore = v; this.emitUpdate();
    });
    y = this.addSliderRow('delayAfter', 0, 5000, 100, step.delayAfter ?? 0, y, (v) => {
      step.delayAfter = v; this.emitUpdate();
    });

    // skipIf condition dropdown
    const condKeys = ConditionRegistry.getKeys();
    if (condKeys.length > 0) {
      const currentSkip = step.skipIf ?? '(none)';
      y = this.addDropdownRow('skipIf', currentSkip, y, () => {
        const items = [
          { label: '(none)', value: '__none__', description: 'Never skip' },
          ...condKeys.map(k => ({ label: k, value: k, description: `Skip if ${k}` })),
        ];
        showSelectionPopup(this.scene, 'Skip Condition', items, (val) => {
          step.skipIf = val === '__none__' ? undefined : val;
          this.emitUpdate(); this.refresh();
        });
      });
    }

    this.contentHeight = y;

    // Restore scroll position (clamped to new content height)
    this.scrollY = Phaser.Math.Clamp(prevScroll, 0, Math.max(0, this.contentHeight - PH + 40));
    this.contentContainer.y = 28 - this.scrollY;
  }

  // ── Per-type builders ──

  // Known speaker i18n keys for the selection popup
  private static readonly KNOWN_SPEAKERS: { label: string; value: string }[] = [
    { label: 'OPERATOR', value: 'cinematic.speaker.operator' },
    { label: 'CDT. MUSTANG', value: 'cinematic.speaker.commander' },
    { label: 'NARRATOR', value: 'cinematic.speaker.narrator' },
  ];

  private buildDialogue(step: any, y: number): number {
    // ── Speaker — selection popup with friendly names ──
    const speakerResolved = t(step.speaker);
    const speakerDisplay = speakerResolved !== step.speaker ? speakerResolved : step.speaker;
    y = this.addDropdownRow('speaker', speakerDisplay, y, () => {
      const items = [
        ...EditorStepPanel.KNOWN_SPEAKERS.map(s => ({
          label: s.label, value: s.value, description: s.value,
        })),
        { label: '✏ Custom...', value: '__custom__', description: 'Type a custom speaker name' },
      ];
      showSelectionPopup(this.scene, 'Speaker', items, (val) => {
        if (val === '__custom__') {
          showTextInputDialog(this.scene, 'Speaker name or i18n key', step.speaker, (v) => {
            step.speaker = v; this.emitUpdate(); this.refresh();
          }, { maxLength: 80 });
        } else {
          step.speaker = val; this.emitUpdate(); this.refresh();
        }
      }, step.speaker);
    });

    // ── Dialogue text — large clickable multiline preview ──
    const resolved = t(step.textKey);
    const hasTranslation = resolved !== step.textKey;
    const displayText = hasTranslation ? resolved : '(click to write dialogue)';
    y = this.addTextPreviewRow('text', displayText, y, () => {
      const currentText = hasTranslation ? resolved : '';
      showTextAreaDialog(this.scene, 'Dialogue Text', currentText, (newText) => {
        // Auto-generate textKey if needed
        const seqId = this.state.sequence?.id ?? 'seq';
        const stepId = step.id ?? 'step';
        const autoKey = `cinematic.${seqId}.${stepId}`;
        // If textKey is still default or already auto-generated, update it
        if (step.textKey === 'NEW_DIALOGUE' || step.textKey === autoKey || !hasTranslation) {
          step.textKey = autoKey;
        }
        // Register the translation for all languages
        addTranslations('en', { [step.textKey]: newText });
        addTranslations('fr', { [step.textKey]: newText });
        this.emitUpdate(); this.refresh();
      }, { maxLength: 500, placeholder: 'Type dialogue text here...' });
    });

    // ── textKey — secondary dev field ──
    y = this.addEditableRow('key', step.textKey, y, (v) => {
      step.textKey = v; this.emitUpdate(); this.refresh();
    });

    // Position toggle: top / center / bottom
    y = this.addToggleRow('position', ['top', 'center', 'bottom'], step.position ?? 'bottom', y, (v) => {
      step.position = v; this.emitUpdate(); this.refresh();
    });

    // Emotion toggle: calm / alert / excited
    y = this.addToggleRow('emotion', ['calm', 'alert', 'excited'], step.emotion ?? 'calm', y, (v) => {
      step.emotion = v; this.emitUpdate(); this.refresh();
    });

    y = this.addSliderRow('autoDismiss', 500, 8000, 250, step.autoDismissMs ?? 3000, y, (v) => {
      step.autoDismissMs = v; this.emitUpdate();
    });

    // World anchor for bubble tail
    const anchor = step.worldAnchor;
    const anchorStr = anchor ? `${anchor.x}, ${anchor.y}` : 'none';
    y = this.addEditableRow('worldAnchor', anchorStr, y, (v) => {
      if (v === 'none' || v === '') { step.worldAnchor = undefined; }
      else {
        const parts = v.split(',').map((s: string) => parseFloat(s.trim()));
        if (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
          step.worldAnchor = { x: parts[0], y: parts[1] };
        }
      }
      this.emitUpdate();
    });
    y = this.addCaptureButton('Capture Anchor', y, () => EventBus.emit(EditorEvents.CAPTURE_START, { type: 'click' }));

    // Portrait selector
    y = this.addPortraitRow(step, y);

    // Portrait display mode (only shown when a portrait is set)
    if (step.portrait) {
      const pdModes = ['inBubble', 'bottomLeft', 'bottomRight'];
      const pdLabels = ['bubble', '◀ left', 'right ▶'];
      const pdCurrent = step.portraitDisplay ?? 'inBubble';
      const pdIdx = pdModes.indexOf(pdCurrent);
      y = this.addToggleRow('portrait pos', pdLabels, pdLabels[pdIdx] ?? pdLabels[0], y, (v) => {
        const idx = pdLabels.indexOf(v);
        step.portraitDisplay = pdModes[idx] ?? 'inBubble';
        this.emitUpdate(); this.refresh();
      });

      y = this.addToggleRow('flip portrait', ['false', 'true'], (step.portraitFlip ?? false) ? 'true' : 'false', y, (v) => {
        step.portraitFlip = v === 'true'; this.emitUpdate();
      });

      // Standing portrait options (only for left/right modes)
      if (step.portraitDisplay && step.portraitDisplay !== 'inBubble') {
        y = this.addSliderRow('portrait size', 0.2, 0.8, 0.05, step.portraitScale ?? 0.4, y, (v) => {
          step.portraitScale = Math.round(v * 100) / 100; this.emitUpdate();
        });

        y = this.addSliderRow('char offset X', -200, 200, 5, (step.portraitOffset?.x ?? 0), y, (v) => {
          step.portraitOffset = { x: v, y: step.portraitOffset?.y ?? 0 }; this.emitUpdate();
        });
        y = this.addSliderRow('char offset Y', -200, 200, 5, (step.portraitOffset?.y ?? 0), y, (v) => {
          step.portraitOffset = { x: step.portraitOffset?.x ?? 0, y: v }; this.emitUpdate();
        });

        y = this.addToggleRow('phone frame', ['false', 'true'], (step.portraitFrame ?? false) ? 'true' : 'false', y, (v) => {
          step.portraitFrame = v === 'true'; this.emitUpdate(); this.refresh();
        });

        if (step.portraitFrame) {
          y = this.addSliderRow('phone size', 0.1, 0.8, 0.05, step.frameScale ?? 0.35, y, (v) => {
            step.frameScale = Math.round(v * 100) / 100; this.emitUpdate();
          });
          y = this.addSliderRow('phone off X', -200, 200, 5, (step.frameOffset?.x ?? 0), y, (v) => {
            step.frameOffset = { x: v, y: step.frameOffset?.y ?? 0 }; this.emitUpdate();
          });
          y = this.addSliderRow('phone off Y', -200, 200, 5, (step.frameOffset?.y ?? 0), y, (v) => {
            step.frameOffset = { x: step.frameOffset?.x ?? 0, y: v }; this.emitUpdate();
          });
        }

        y = this.addToggleRow('transition', ['slide', 'fade', 'pop', 'none'], step.portraitTransition ?? 'slide', y, (v) => {
          step.portraitTransition = v; this.emitUpdate();
        });
      }
    }

    // ── BD bubble customization ──
    y += 6;
    y = this.addLabel(PAD, y, '── Bubble Style ──', '#555566');

    // Template dropdown
    y = this.addBubbleTemplateRow(step, y);

    // Color pickers
    y = this.addColorRow('borderColor', step.borderColor, BORDER_COLOR_PRESETS, y, (v) => {
      step.borderColor = v; this.emitUpdate(); this.refresh();
    });
    y = this.addColorRow('fillColor', step.fillColor, FILL_COLOR_PRESETS, y, (v) => {
      step.fillColor = v; this.emitUpdate(); this.refresh();
    });
    y = this.addColorRow('textColor', step.textColor, TEXT_COLOR_PRESETS, y, (v) => {
      step.textColor = v; this.emitUpdate(); this.refresh();
    });

    // Bubble position (read-only + reset)
    y = this.addPosResetRow('bubblePos', step.bubblePos, y, () => {
      step.bubblePos = undefined; this.emitUpdate(); this.refresh();
    });

    // Tail tip (read-only + reset)
    y = this.addPosResetRow('tailTip', step.tailTip, y, () => {
      step.tailTip = undefined; this.emitUpdate(); this.refresh();
    });

    // ── Optional spotlight highlight ──
    y += 6;
    y = this.addLabel(PAD, y, '── Highlight ──', '#555566');

    const hasHighlight = !!step.highlightTarget;
    const hlTargetStr = hasHighlight
      ? (typeof step.highlightTarget === 'string' ? step.highlightTarget : 'custom rect')
      : 'none';
    const hlMeta = hasHighlight
      ? EditorStepPanel.SPOTLIGHT_TARGET_METAS.find(m => m.value === hlTargetStr)
      : null;

    y = this.addDropdownRow('highlightTarget', hlMeta?.label ?? hlTargetStr, y, () => {
      const options = [
        { label: '❌ None', value: '__none__', description: 'No highlight' },
        ...EditorStepPanel.SPOTLIGHT_TARGET_METAS,
      ];
      showSelectionPopup(this.scene, 'Highlight Target', options, (val) => {
        if (val === '__none__') {
          step.highlightTarget = undefined;
        } else if (val === 'custom') {
          // Center the default rect on what the camera currently sees
          const cam = this.state.hostScene?.cameras?.main;
          const cx = cam ? cam.scrollX + cam.width / 2 : 400;
          const cy = cam ? cam.scrollY + cam.height / 2 : 300;
          const rw = 100, rh = 60;
          step.highlightTarget = { x: Math.round(cx - rw / 2), y: Math.round(cy - rh / 2), w: rw, h: rh };
        } else {
          step.highlightTarget = val;
        }
        this.emitUpdate(); this.refresh();
      });
    });

    if (hasHighlight) {
      y = this.addToggleRow('hlScreenSpace', ['false', 'true'], step.highlightScreenSpace ? 'true' : 'false', y, (v) => {
        step.highlightScreenSpace = v === 'true'; this.emitUpdate(); this.refresh();
      });

      // Custom rect coords
      if (typeof step.highlightTarget === 'object' && !Array.isArray(step.highlightTarget)) {
        const r = step.highlightTarget;
        y = this.addEditableRow('hl x,y', `${r.x}, ${r.y}`, y, (v) => {
          const parts = v.split(',').map((s: string) => parseFloat(s.trim()));
          if (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
            step.highlightTarget = { ...step.highlightTarget, x: parts[0], y: parts[1] };
            this.emitUpdate();
          }
        });
        y = this.addEditableRow('hl w,h', `${r.w}, ${r.h}`, y, (v) => {
          const parts = v.split(',').map((s: string) => parseFloat(s.trim()));
          if (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
            step.highlightTarget = { ...step.highlightTarget, w: parts[0], h: parts[1] };
            this.emitUpdate();
          }
        });
        y = this.addCaptureButton(
          step.highlightScreenSpace ? 'Capture Rect (UI)' : 'Capture Rect (Map)',
          y,
          () => EventBus.emit(EditorEvents.CAPTURE_START, { type: 'rect', screenSpace: !!step.highlightScreenSpace }),
        );
      }

      y = this.addSliderRow('hlDimAlpha', 0, 1, 0.05, step.highlightDimAlpha ?? 0.55, y, (v) => {
        step.highlightDimAlpha = Math.round(v * 100) / 100; this.emitUpdate();
      });

      y = this.addColorRow('hlColor', step.highlightColor, BORDER_COLOR_PRESETS, y, (v) => {
        step.highlightColor = v; this.emitUpdate(); this.refresh();
      });

      y = this.addToggleRow('hlPulse', ['false', 'true'], (step.highlightPulse ?? true) ? 'true' : 'false', y, (v) => {
        step.highlightPulse = v === 'true'; this.emitUpdate();
      });
    }

    return y;
  }

  private static readonly CAMERA_TARGET_METAS = [
    { label: '🏠 Base',   value: 'base',   description: 'Center of base positions' },
    { label: '👾 Spawn',  value: 'spawn',  description: 'Center of spawn positions' },
    { label: '🛤 Path',   value: 'path',   description: 'Midpoint of first path' },
    { label: '📍 Custom', value: 'custom', description: 'Manual coordinates' },
  ];

  private buildCameraPan(step: any, y: number): number {
    const isNamed = typeof step.target === 'string';
    const currentValue = isNamed ? step.target : 'custom';
    const currentMeta = EditorStepPanel.CAMERA_TARGET_METAS.find(m => m.value === currentValue);
    y = this.addDropdownRow('target', currentMeta?.label ?? currentValue, y, () => {
      showSelectionPopup(this.scene, 'Camera Target', EditorStepPanel.CAMERA_TARGET_METAS, (val) => {
        if (val === 'custom') {
          if (typeof step.target === 'string') step.target = { x: 600, y: 400 };
        } else {
          step.target = val;
        }
        this.emitUpdate(); this.refresh();
      });
    });
    // Show editable coords for custom targets
    if (!isNamed && step.target) {
      y = this.addCoordsRow('coords', step.target, y, (x, vy) => {
        step.target = { x, y: vy }; this.emitUpdate();
      });
    }
    y = this.addCaptureButton('Capture Cam', y, () => EventBus.emit(EditorEvents.CAPTURE_START, { type: 'camera' }));
    y = this.addSliderRow('zoom', 0.3, 5, 0.1, step.zoom ?? 1.5, y, (v) => {
      step.zoom = Math.round(v * 100) / 100; this.emitUpdate();
    });
    y = this.addSliderRow('duration', 100, 5000, 100, step.duration ?? 800, y, (v) => {
      step.duration = v; this.emitUpdate();
    });
    return y;
  }

  private static readonly CLICK_TARGET_METAS = [
    { label: '🔲 Placement Tile', value: 'placementTile', description: 'First placement tile on map' },
    { label: '▶ Start Button',    value: 'startButton',   description: 'Wave start button in UI' },
    { label: '📍 Custom',         value: 'custom',        description: 'Manual coordinates' },
  ];

  private buildFakeClick(step: any, y: number): number {
    // Screen space toggle (UI vs game world)
    y = this.addToggleRow('screenSpace', ['false', 'true'], step.screenSpace ? 'true' : 'false', y, (v) => {
      step.screenSpace = v === 'true'; this.emitUpdate(); this.refresh();
    });

    const isNamed = typeof step.target === 'string';
    const currentValue = isNamed ? step.target : 'custom';
    const currentMeta = EditorStepPanel.CLICK_TARGET_METAS.find(m => m.value === currentValue);
    y = this.addDropdownRow('target', currentMeta?.label ?? currentValue, y, () => {
      showSelectionPopup(this.scene, 'Click Target', EditorStepPanel.CLICK_TARGET_METAS, (val) => {
        if (val === 'custom') {
          if (typeof step.target === 'string') step.target = { x: 600, y: 400 };
        } else {
          step.target = val;
        }
        this.emitUpdate(); this.refresh();
      });
    });
    // Show editable coords for custom targets
    if (!isNamed && step.target) {
      const spaceLabel = step.screenSpace ? '(screen)' : '(world)';
      y = this.addCoordsRow(`coords ${spaceLabel}`, step.target, y, (x, vy) => {
        step.target = { x, y: vy }; this.emitUpdate();
      });
    }
    const captureLabel = step.screenSpace ? 'Capture Click (UI)' : 'Capture Click (Map)';
    y = this.addCaptureButton(captureLabel, y, () => EventBus.emit(EditorEvents.CAPTURE_START, { type: 'click', screenSpace: step.screenSpace }));
    y = this.addSliderRow('holdMs', 100, 3000, 50, step.holdMs ?? 500, y, (v) => {
      step.holdMs = v; this.emitUpdate();
    });
    y = this.addToggleRow('dispatch', ['false', 'true'], step.dispatch ? 'true' : 'false', y, (v) => {
      step.dispatch = v === 'true'; this.emitUpdate();
    });
    return y;
  }

  // ── Action step: dropdown + per-action parameter forms ──

  private static readonly ACTION_METAS = [
    { label: '⚔ Spawn Enemies', value: 'spawnEnemies', description: 'Spawn demo enemies on path' },
    { label: '🤖 Place Robot',   value: 'placeRobot',   description: 'Auto-place a robot on tile' },
    { label: '▶ Start Wave',     value: 'startWave',    description: 'Begin wave combat' },
    { label: '💥 Destroy Enemies', value: 'destroyEnemy', description: 'Remove all demo enemies' },
  ];

  private static readonly ACTION_DEFAULTS: Record<string, Record<string, unknown>> = {
    spawnEnemies: { count: 3, type: 'grunt', grade: 'normal', pathIndex: 0 },
    placeRobot:   { col: 5, row: 7, preset: '' },
    startWave:    {},
    destroyEnemy: {},
  };

  private buildAction(step: any, y: number): number {
    // Action type selector
    const current = EditorStepPanel.ACTION_METAS.find(m => m.value === step.action);
    const displayLabel = current ? current.label : step.action;
    y = this.addDropdownRow('action', displayLabel, y, () => {
      showSelectionPopup(this.scene, 'Action Type', EditorStepPanel.ACTION_METAS, (val) => {
        if (val !== step.action) {
          step.action = val;
          step.params = { ...(EditorStepPanel.ACTION_DEFAULTS[val] ?? {}) };
          this.emitUpdate();
          this.refresh();
        }
      });
    });

    // Per-action parameter forms
    const p = step.params ?? {};
    switch (step.action) {
      case 'spawnEnemies': y = this.buildActionSpawnEnemies(step, p, y); break;
      case 'placeRobot':   y = this.buildActionPlaceRobot(step, p, y); break;
      case 'startWave':
      case 'destroyEnemy':
        y = this.addLabel(PAD, y, 'No parameters', '#555566');
        break;
      default:
        // Fallback: raw JSON for unknown actions
        y = this.addEditableRow('params', JSON.stringify(p), y, (v) => {
          try { step.params = JSON.parse(v); } catch (e) { console.warn('[EditorStepPanel] params parse failed:', e); }
          this.emitUpdate();
        });
    }
    return y;
  }

  private buildActionSpawnEnemies(step: any, p: any, y: number): number {
    y = this.addSliderRow('count', 1, 20, 1, p.count ?? 3, y, (v) => {
      step.params = { ...step.params, count: v }; this.emitUpdate();
    });
    // Enemy type dropdown
    const types = Object.values(EnemyType);
    y = this.addDropdownRow('type', p.type ?? 'grunt', y, () => {
      const items = types.map(t => ({ label: t, value: t, description: t }));
      showSelectionPopup(this.scene, 'Enemy Type', items, (val) => {
        step.params = { ...step.params, type: val }; this.emitUpdate(); this.refresh();
      });
    });
    // Grade dropdown
    const grades = Object.values(EnemyGrade);
    y = this.addDropdownRow('grade', p.grade ?? 'normal', y, () => {
      const items = grades.map(g => ({ label: g, value: g, description: g }));
      showSelectionPopup(this.scene, 'Enemy Grade', items, (val) => {
        step.params = { ...step.params, grade: val }; this.emitUpdate(); this.refresh();
      });
    });
    y = this.addSliderRow('pathIndex', 0, 3, 1, p.pathIndex ?? 0, y, (v) => {
      step.params = { ...step.params, pathIndex: v }; this.emitUpdate();
    });
    return y;
  }

  private buildActionPlaceRobot(step: any, p: any, y: number): number {
    y = this.addSliderRow('col', 0, MAP_COLS - 1, 1, p.col ?? 5, y, (v) => {
      step.params = { ...step.params, col: v }; this.emitUpdate();
    });
    y = this.addSliderRow('row', 0, MAP_ROWS - 1, 1, p.row ?? 7, y, (v) => {
      step.params = { ...step.params, row: v }; this.emitUpdate();
    });
    y = this.addEditableRow('preset', p.preset ?? '', y, (v) => {
      step.params = { ...step.params, preset: v }; this.emitUpdate();
    });
    y = this.addCaptureButton('Capture Tile', y, () => EventBus.emit(EditorEvents.CAPTURE_START, { type: 'click' }));
    return y;
  }

  private static readonly WAIT_EVENT_METAS = [
    { label: '(none — timeout only)', value: '',                          description: 'Just wait for timeout' },
    { label: '🏁 Wave Completed',     value: 'waveCompleted',             description: 'Current wave ends' },
    { label: '▶ Wave Started',        value: 'waveStarted',              description: 'Wave combat begins' },
    { label: '👾 Enemy Spawned',      value: 'enemySpawned',             description: 'An enemy appears' },
    { label: '💥 Enemy Destroyed',    value: 'enemyDestroyed',           description: 'An enemy is killed' },
    { label: '🤖 Robot Placed',       value: 'robotPlaced',              description: 'Player places a robot' },
    { label: '🔫 Robot Fired',        value: 'robotFired',               description: 'A robot shoots' },
    { label: '💬 Bubble Dismissed',   value: 'cinematic:bubbleDismissed', description: 'Dialogue bubble closed' },
    { label: '📋 Recap Closed',       value: 'cinematic:recapClosed',     description: 'Recap panel confirmed' },
    { label: '✏ Custom Event',        value: '__custom__',               description: 'Type a custom event name' },
  ];

  private buildWait(step: any, y: number): number {
    const currentEvent = step.waitEvent ?? '';
    const knownMeta = EditorStepPanel.WAIT_EVENT_METAS.find(m => m.value === currentEvent);
    const displayLabel = knownMeta ? knownMeta.label : `✏ ${currentEvent}`;
    y = this.addDropdownRow('waitEvent', displayLabel, y, () => {
      showSelectionPopup(this.scene, 'Wait Event', EditorStepPanel.WAIT_EVENT_METAS, (val) => {
        if (val === '__custom__') {
          showTextInputDialog(this.scene, 'Custom Event', currentEvent, (customVal) => {
            step.waitEvent = customVal || undefined;
            this.emitUpdate(); this.refresh();
          });
        } else {
          step.waitEvent = val || undefined;
          this.emitUpdate(); this.refresh();
        }
      });
    });
    y = this.addSliderRow('timeoutMs', 500, 30000, 500, step.timeoutMs ?? 2000, y, (v) => {
      step.timeoutMs = v; this.emitUpdate();
    });

    y = this.addToggleRow('simulateCombat', ['false', 'true'], (step.simulateCombat ?? false) ? 'true' : 'false', y, (v) => {
      step.simulateCombat = v === 'true'; this.emitUpdate(); this.refresh();
    });

    if (step.simulateCombat) {
      y = this.addReadonlyRow('info', 'Starts wave — robots & enemies fight. No score/save impact.', y, '#888899');
    }

    return y;
  }

  private buildRecap(step: any, y: number): number {
    y = this.addEditableRow('titleKey', step.titleKey, y, (v) => { step.titleKey = v; this.emitUpdate(); });
    y = this.addEditableRow('confirmKey', step.confirmKey, y, (v) => { step.confirmKey = v; this.emitUpdate(); });

    // Items list editor
    y = this.addLabel(PAD, y, 'items:', '#888888');
    const items: { textKey: string; icon?: string }[] = step.items ?? [];
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      // Item row: index + editable textKey + delete button
      const idx = i;
      const lbl = this.scene.add.text(PAD, y, `  ${i + 1}.`, { fontSize: '10px', color: '#555566', fontFamily: 'monospace' });
      const val = this.scene.add.text(PAD + 28, y, item.textKey.length > 16 ? item.textKey.substring(0, 16) + '..' : item.textKey, {
        fontSize: '10px', color: '#cccccc', fontFamily: 'monospace',
      }).setInteractive({ useHandCursor: true });
      val.on('pointerdown', () => {
        showTextInputDialog(this.scene, `Item ${idx + 1} textKey`, item.textKey, (newVal) => {
          items[idx].textKey = newVal;
          this.emitUpdate(); this.refresh();
        });
      });
      // Delete button
      const del = this.scene.add.text(STEP_PANEL_W - PAD - 16, y, '✕', {
        fontSize: '11px', color: '#ff4444', fontFamily: 'monospace',
      }).setInteractive({ useHandCursor: true });
      del.on('pointerdown', () => {
        items.splice(idx, 1);
        step.items = items;
        this.emitUpdate(); this.refresh();
      });
      this.contentContainer.add([lbl, val, del]);
      y += ROW_H;
    }
    // Add item button
    const addBtn = createSmallTechButton(this.scene, PAD + 70, y + 4, '+ Add Item', TECH.ACCENT, () => {
      items.push({ textKey: 'NEW_ITEM' });
      step.items = items;
      this.emitUpdate(); this.refresh();
    }, 1);
    this.contentContainer.add(addBtn);
    y += 28;

    return y;
  }

  // ── Spotlight step ──

  private static readonly SPOTLIGHT_TARGET_METAS = [
    // Single targets
    { label: '🔲 Placement Tile',  value: 'placementTile',      description: 'First placement tile on map' },
    { label: '▶ Start Button',     value: 'startButton',        description: 'Wave start button in UI' },
    { label: '📍 Custom Rect',     value: 'custom',             description: 'Manual {x,y,w,h} rectangle' },
    // Tile multi-targets
    { label: '🟨 All Placements',  value: 'allPlacementTiles',  description: 'All placement + grille tiles' },
    { label: '🟧 All Path Tiles',  value: 'allPathTiles',       description: 'All path tiles' },
    { label: '🧱 All Walls',       value: 'allWalls',           description: 'All wall tiles' },
    { label: '🌊 All Special',     value: 'allSpecialTiles',    description: 'Mud, water, magma tiles' },
    { label: '💔 Broken Places',   value: 'allBrokenPlacements', description: 'All broken placement tiles' },
    // Entity multi-targets
    { label: '👾 All Enemies',     value: 'allEnemies',         description: 'All alive enemies' },
    { label: '🔴 All Grunts',      value: 'allGrunts',          description: 'All alive grunts' },
    { label: '🟠 All Runners',     value: 'allRunners',         description: 'All alive runners' },
    { label: '🟣 All Tanks',       value: 'allTanks',           description: 'All alive tanks' },
    { label: '🟢 All Swarms',      value: 'allSwarms',          description: 'All alive swarm units' },
    { label: '🟤 All Brutes',      value: 'allBrutes',          description: 'All alive brutes' },
    { label: '🔵 All Phantoms',    value: 'allPhantoms',        description: 'All alive phantoms' },
    { label: '🪰 All Drones',      value: 'allDrones',          description: 'All alive drones' },
    { label: '💥 All Bombers',     value: 'allBombers',         description: 'All alive bombers' },
    { label: '🐝 All Wasps',       value: 'allWasps',           description: 'All alive wasps' },
    { label: '👻 All Specters',    value: 'allSpecters',        description: 'All alive specters' },
    // Robot + structural
    { label: '🤖 All Robots',      value: 'allRobots',          description: 'All alive player robots' },
    { label: '🏠 All Bases',       value: 'allBases',           description: 'All base positions' },
    { label: '🚪 All Spawns',      value: 'allSpawns',          description: 'All enemy spawn points' },
  ];

  private buildSpotlight(step: any, y: number): number {
    // Target selector
    const isNamed = typeof step.target === 'string';
    const currentValue = isNamed ? step.target : 'custom';
    const currentMeta = EditorStepPanel.SPOTLIGHT_TARGET_METAS.find(m => m.value === currentValue);
    y = this.addDropdownRow('target', currentMeta?.label ?? currentValue, y, () => {
      showSelectionPopup(this.scene, 'Spotlight Target', EditorStepPanel.SPOTLIGHT_TARGET_METAS, (val) => {
        if (val === 'custom') {
          if (typeof step.target === 'string') {
            const cam = this.state.hostScene?.cameras?.main;
            const cx = cam ? cam.scrollX + cam.width / 2 : 400;
            const cy = cam ? cam.scrollY + cam.height / 2 : 300;
            step.target = { x: Math.round(cx - 50), y: Math.round(cy - 30), w: 100, h: 60 };
          }
        } else {
          step.target = val;
        }
        this.emitUpdate(); this.refresh();
      });
    });

    // Screen space toggle — must be before capture so capture reads correct mode
    y = this.addToggleRow('screenSpace', ['false', 'true'], step.screenSpace ? 'true' : 'false', y, (v) => {
      step.screenSpace = v === 'true'; this.emitUpdate(); this.refresh();
    });

    // Custom rect coords (x, y, w, h)
    if (!isNamed && typeof step.target === 'object') {
      const coordLabel = step.screenSpace ? 'screen' : 'world';
      const r = step.target;
      y = this.addEditableRow(`x,y (${coordLabel})`, `${r.x}, ${r.y}`, y, (v) => {
        const parts = v.split(',').map((s: string) => parseFloat(s.trim()));
        if (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
          step.target = { ...step.target, x: parts[0], y: parts[1] };
          this.emitUpdate();
        }
      });
      y = this.addEditableRow('w,h', `${r.w}, ${r.h}`, y, (v) => {
        const parts = v.split(',').map((s: string) => parseFloat(s.trim()));
        if (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
          step.target = { ...step.target, w: parts[0], h: parts[1] };
          this.emitUpdate();
        }
      });
      y = this.addCaptureButton(
        step.screenSpace ? 'Capture Rect (UI)' : 'Capture Rect (Map)',
        y,
        () => EventBus.emit(EditorEvents.CAPTURE_START, { type: 'rect', screenSpace: !!step.screenSpace }),
      );
    }

    y = this.addSliderRow('dimAlpha', 0, 1, 0.05, step.dimAlpha ?? 0.55, y, (v) => {
      step.dimAlpha = Math.round(v * 100) / 100; this.emitUpdate();
    });

    y = this.addColorRow('highlightColor', step.highlightColor, BORDER_COLOR_PRESETS, y, (v) => {
      step.highlightColor = v; this.emitUpdate(); this.refresh();
    });

    y = this.addToggleRow('pulse', ['false', 'true'], (step.pulse ?? true) ? 'true' : 'false', y, (v) => {
      step.pulse = v === 'true'; this.emitUpdate();
    });

    y = this.addEditableRow('labelKey', step.labelKey ?? '', y, (v) => {
      step.labelKey = v || undefined; this.emitUpdate();
    });

    y = this.addToggleRow('labelPosition', ['top', 'bottom', 'left', 'right'], step.labelPosition ?? 'bottom', y, (v) => {
      step.labelPosition = v; this.emitUpdate();
    });

    y = this.addToggleRow('arrowDir', ['none', 'up', 'down', 'left', 'right'], step.arrowDir ?? 'none', y, (v) => {
      step.arrowDir = v === 'none' ? undefined : v; this.emitUpdate();
    });

    y = this.addToggleRow('advanceOn', ['click', 'event'], step.advanceOn ?? 'click', y, (v) => {
      step.advanceOn = v; this.emitUpdate(); this.refresh();
    });

    // Show waitEvent dropdown when advanceOn = 'event'
    if (step.advanceOn === 'event') {
      const currentEvent = step.waitEvent ?? '';
      const knownMeta = EditorStepPanel.WAIT_EVENT_METAS.find(m => m.value === currentEvent);
      const displayLabel = knownMeta ? knownMeta.label : `✏ ${currentEvent}`;
      y = this.addDropdownRow('waitEvent', displayLabel, y, () => {
        showSelectionPopup(this.scene, 'Wait Event', EditorStepPanel.WAIT_EVENT_METAS, (val) => {
          if (val === '__custom__') {
            showTextInputDialog(this.scene, 'Custom Event', currentEvent, (customVal) => {
              step.waitEvent = customVal || undefined;
              this.emitUpdate(); this.refresh();
            });
          } else {
            step.waitEvent = val || undefined;
            this.emitUpdate(); this.refresh();
          }
        });
      });
    }

    y = this.addSliderRow('timeoutMs', 0, 30000, 500, step.timeoutMs ?? 0, y, (v) => {
      step.timeoutMs = v || undefined; this.emitUpdate();
    });

    y = this.addToggleRow('restrictInput', ['false', 'true'], (step.restrictInput ?? true) ? 'true' : 'false', y, (v) => {
      step.restrictInput = v === 'true'; this.emitUpdate();
    });

    y = this.addCaptureButton('Capture Spotlight', y, () => EventBus.emit(EditorEvents.CAPTURE_START, { type: 'click' }));

    return y;
  }

  // ── UI helpers ──

  private addLabel(x: number, y: number, text: string, color: string): number {
    const t = this.scene.add.text(x, y, text, { fontSize: '11px', color, fontFamily: 'monospace' });
    this.contentContainer.add(t);
    return y + ROW_H;
  }

  private addEditableRow(label: string, value: string, y: number, onChange: (v: string) => void): number {
    const lbl = this.scene.add.text(PAD, y, label, { fontSize: '10px', color: '#888888', fontFamily: 'monospace' });
    const valDisplay = value.length > 18 ? value.substring(0, 18) + '..' : value;
    const val = this.scene.add.text(PAD + LABEL_W, y, valDisplay, {
      fontSize: '10px', color: '#cccccc', fontFamily: 'monospace',
    }).setInteractive({ useHandCursor: true });
    val.on('pointerdown', () => {
      showTextInputDialog(this.scene, label, value, (newVal) => {
        onChange(newVal);
        this.refresh();
      });
    });
    this.contentContainer.add([lbl, val]);
    this.addHelpIcon(label, STEP_PANEL_W - PAD - 14, y);
    return y + ROW_H;
  }

  private addDropdownRow(label: string, displayValue: string, y: number, onClick: () => void): number {
    const lbl = this.scene.add.text(PAD, y, label, { fontSize: '10px', color: '#888888', fontFamily: 'monospace' });
    const truncated = displayValue.length > 16 ? displayValue.substring(0, 16) + '..' : displayValue;
    const val = this.scene.add.text(PAD + LABEL_W, y, `▼ ${truncated}`, {
      fontSize: '10px', color: '#ffcc00', fontFamily: 'monospace',
    }).setInteractive({ useHandCursor: true });
    val.on('pointerdown', onClick);
    this.contentContainer.add([lbl, val]);
    this.addHelpIcon(label, STEP_PANEL_W - PAD - 14, y);
    return y + ROW_H;
  }

  private addReadonlyRow(label: string, value: string, y: number, color = '#888888'): number {
    const lbl = this.scene.add.text(PAD, y, label, { fontSize: '10px', color: '#888888', fontFamily: 'monospace' });
    const display = value.length > 28 ? value.substring(0, 28) + '..' : value;
    const val = this.scene.add.text(PAD + LABEL_W, y, display, {
      fontSize: '10px', color, fontFamily: 'monospace', wordWrap: { width: STEP_PANEL_W - LABEL_W - PAD * 2 },
    });
    this.contentContainer.add([lbl, val]);
    // Height depends on wrap
    const lines = Math.ceil(val.height / 14);
    return y + Math.max(ROW_H, lines * 14 + 4);
  }

  /** Large clickable multiline text preview — for dialogue editing. */
  private addTextPreviewRow(label: string, value: string, y: number, onClick: () => void): number {
    const lbl = this.scene.add.text(PAD, y, label, { fontSize: '10px', color: '#888888', fontFamily: 'monospace' });
    this.contentContainer.add(lbl);
    y += 14;
    const maxW = STEP_PANEL_W - PAD * 2;
    const isPlaceholder = value.startsWith('(');
    const val = this.scene.add.text(PAD, y, value, {
      fontSize: '10px', color: isPlaceholder ? '#666688' : '#ccccee', fontFamily: 'monospace',
      fontStyle: isPlaceholder ? 'italic' : 'normal',
      wordWrap: { width: maxW }, lineSpacing: 2,
    }).setInteractive({ useHandCursor: true });
    val.on('pointerdown', onClick);
    // Background highlight on hover
    const bgH = Math.max(32, val.height + 6);
    const bg = this.scene.add.rectangle(PAD + maxW / 2, y + bgH / 2 - 2, maxW + 4, bgH, 0x222244, 0)
      .setStrokeStyle(1, 0x334466, 0);
    val.on('pointerover', () => { bg.setFillStyle(0x222244, 0.4); bg.setStrokeStyle(1, 0x4466aa, 0.6); });
    val.on('pointerout', () => { bg.setFillStyle(0x222244, 0); bg.setStrokeStyle(1, 0x334466, 0); });
    this.contentContainer.add([bg, val]);
    const lines = Math.ceil(val.height / 14);
    return y + Math.max(34, lines * 14 + 8);
  }

  private addToggleRow(label: string, options: string[], current: string, y: number, onChange: (v: string) => void): number {
    const lbl = this.scene.add.text(PAD, y, label, { fontSize: '10px', color: '#888888', fontFamily: 'monospace' });
    this.contentContainer.add(lbl);
    this.addHelpIcon(label, STEP_PANEL_W - PAD - 14, y);
    let bx = PAD + LABEL_W;
    for (const opt of options) {
      const isActive = opt === current;
      const bg = this.scene.add.graphics();
      const w = opt.length * 7 + 12;
      bg.fillStyle(isActive ? 0x334466 : 0x181830, 1);
      bg.fillRoundedRect(bx, y - 2, w, 18, 4);
      bg.lineStyle(1, isActive ? TECH.ACCENT : TECH.DIM, isActive ? 0.8 : 0.3);
      bg.strokeRoundedRect(bx, y - 2, w, 18, 4);
      const txt = this.scene.add.text(bx + 6, y, opt, {
        fontSize: '10px', color: isActive ? '#ffcc00' : '#666688', fontFamily: 'monospace',
      }).setInteractive({ useHandCursor: true });
      txt.on('pointerdown', () => onChange(opt));
      this.contentContainer.add([bg, txt]);
      bx += w + 4;
    }
    return y + ROW_H;
  }

  private addSliderRow(label: string, min: number, max: number, step: number, value: number, y: number, onChange: (v: number) => void): number {
    const trackW = STEP_PANEL_W - LABEL_W - PAD * 3 - 40;
    const slider = createTechSlider(this.scene, PAD, y, label, min, max, step, value, (v) => {
      onChange(v);
    }, trackW);
    this.contentContainer.add(slider);
    this.addHelpIcon(label, STEP_PANEL_W - PAD - 14, y - 4);
    return y + 32;
  }

  private addCoordsRow(label: string, target: { x: number; y: number }, y: number, onChange: (x: number, y: number) => void): number {
    const coordStr = `${target.x}, ${target.y}`;
    return this.addEditableRow(label, coordStr, y, (v: string) => {
      const parts = v.split(',').map((s: string) => parseFloat(s.trim()));
      if (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
        onChange(parts[0], parts[1]);
      }
    });
  }

  /** Add a small (?) icon that shows a tooltip on hover */
  private addHelpIcon(paramKey: string, x: number, y: number): void {
    const tip = PARAM_TIPS[paramKey];
    if (!tip) return;
    const icon = this.scene.add.text(x, y, '?', {
      fontSize: '9px', color: '#444466', fontFamily: 'monospace',
      backgroundColor: '#222244', padding: { x: 3, y: 1 },
    }).setInteractive({ useHandCursor: true });
    icon.on('pointerover', () => {
      icon.setColor('#ffcc00');
      this.showTooltip(icon.x + 16, icon.y, tip);
    });
    icon.on('pointerout', () => {
      icon.setColor('#444466');
      this.hideTooltip();
    });
    this.contentContainer.add(icon);
  }

  private showTooltip(x: number, y: number, text: string): void {
    this.hideTooltip();
    // Clamp tooltip within panel bounds
    const maxW = STEP_PANEL_W - PAD * 2 - 20;
    this.tooltip = this.scene.add.text(x, y, text, {
      fontSize: '9px', color: '#cccccc', fontFamily: 'monospace',
      backgroundColor: '#1a1a3a', padding: { x: 6, y: 4 },
      wordWrap: { width: maxW },
    }).setDepth(50);
    // Keep within panel right edge
    if (this.tooltip.x + this.tooltip.width > STEP_PANEL_W - PAD) {
      this.tooltip.x = STEP_PANEL_W - PAD - this.tooltip.width;
    }
    this.contentContainer.add(this.tooltip);
  }

  private hideTooltip(): void {
    if (this.tooltip) { this.tooltip.destroy(); this.tooltip = null; }
  }

  private addCaptureButton(label: string, y: number, onClick: () => void): number {
    const btn = createSmallTechButton(this.scene, PAD + 70, y + 10, label, TECH.ACCENT, onClick, 1);
    this.contentContainer.add(btn);
    return y + 28;
  }

  private static readonly PORTRAIT_KEYS = [
    '', 'char_colossus', 'char_cyberlord', 'char_gamer_kiddo', 'char_influencer',
    'char_mustang', 'char_old_mercenary', 'char_commandant',
    'char_shneiper', 'char_major', 'char_secretary', 'char_technomonk', 'char_voice_assistant',
  ];

  private addPortraitRow(step: any, y: number): number {
    const keys = EditorStepPanel.PORTRAIT_KEYS;
    const current = step.portrait ?? '';
    const idx = keys.indexOf(current);
    const displayName = current ? current.replace('char_', '') : 'none';

    const label = this.scene.add.text(PAD, y, 'portrait', {
      fontSize: '10px', color: '#888888', fontFamily: 'monospace',
    });

    // Display name (clickable for popup)
    const nameText = this.scene.add.text(PAD + LABEL_W, y, displayName, {
      fontSize: '10px', color: current ? '#ffcc00' : '#555566', fontFamily: 'monospace',
    }).setInteractive({ useHandCursor: true });
    nameText.on('pointerdown', () => {
      const items = keys.map(k => ({
        label: k || '(none)', value: k,
        description: k ? k.replace('char_', '') : 'Remove portrait',
      }));
      showSelectionPopup(this.scene, 'Portrait', items, (v) => {
        step.portrait = v || undefined;
        this.emitUpdate(); this.refresh();
      });
    });
    this.contentContainer.add([label, nameText]);
    y += ROW_H;

    // ◀ left / right ▶ toggle-style buttons
    const btnW = 80;
    const btnH = 22;
    const gap = 8;
    const bx = PAD + LABEL_W;

    const leftBtn = this.scene.add.container(bx, y);
    const leftBg = this.scene.add.graphics();
    leftBg.fillStyle(0x1a1a2e, 1); leftBg.fillRoundedRect(0, 0, btnW, btnH, 4);
    leftBg.lineStyle(1, 0x444466, 0.8); leftBg.strokeRoundedRect(0, 0, btnW, btnH, 4);
    const leftTxt = this.scene.add.text(btnW / 2, btnH / 2, '◀ left', {
      fontSize: '10px', color: '#8888aa', fontFamily: 'monospace',
    }).setOrigin(0.5);
    leftBtn.add([leftBg, leftTxt]);
    const leftZone = this.scene.add.rectangle(btnW / 2, btnH / 2, btnW, btnH, 0x000000, 0).setInteractive({ useHandCursor: true });
    leftBtn.add(leftZone);
    leftZone.on('pointerdown', () => {
      const newIdx = (idx <= 0) ? keys.length - 1 : idx - 1;
      step.portrait = keys[newIdx] || undefined;
      this.emitUpdate(); this.refresh();
    });

    const rightBtn = this.scene.add.container(bx + btnW + gap, y);
    const rightBg = this.scene.add.graphics();
    rightBg.fillStyle(0x1a1a2e, 1); rightBg.fillRoundedRect(0, 0, btnW, btnH, 4);
    rightBg.lineStyle(1, 0x444466, 0.8); rightBg.strokeRoundedRect(0, 0, btnW, btnH, 4);
    const rightTxt = this.scene.add.text(btnW / 2, btnH / 2, 'right ▶', {
      fontSize: '10px', color: '#8888aa', fontFamily: 'monospace',
    }).setOrigin(0.5);
    rightBtn.add([rightBg, rightTxt]);
    const rightZone = this.scene.add.rectangle(btnW / 2, btnH / 2, btnW, btnH, 0x000000, 0).setInteractive({ useHandCursor: true });
    rightBtn.add(rightZone);
    rightZone.on('pointerdown', () => {
      const newIdx = (idx >= keys.length - 1) ? 0 : idx + 1;
      step.portrait = keys[newIdx] || undefined;
      this.emitUpdate(); this.refresh();
    });

    this.contentContainer.add([leftBtn, rightBtn]);
    return y + btnH + 6;
  }

  /** Bubble template dropdown */
  private addBubbleTemplateRow(step: any, y: number): number {
    const current: BubbleTemplate = step.bubbleTemplate ?? 'trueBD';
    const tmpl = BUBBLE_TEMPLATES[current];
    const displayLabel = `${tmpl.label} — ${tmpl.description}`;
    return this.addDropdownRow('bubbleTemplate', displayLabel, y, () => {
      const items = (Object.values(BUBBLE_TEMPLATES)).map(t => ({
        label: t.label,
        value: t.id,
        description: t.description,
      }));
      showSelectionPopup(this.scene, 'Bubble Template', items, (val) => {
        step.bubbleTemplate = val;
        this.emitUpdate(); this.refresh();
      });
    });
  }

  /** Color preset picker row: swatch + label, click opens preset popup + custom hex */
  private addColorRow(
    label: string,
    currentValue: number | undefined,
    presets: { label: string; value: number }[],
    y: number,
    onChange: (v: number | undefined) => void,
  ): number {
    const lbl = this.scene.add.text(PAD, y, label, {
      fontSize: '10px', color: '#888888', fontFamily: 'monospace',
    });
    // Color swatch
    const swatchX = PAD + LABEL_W;
    const swatch = this.scene.add.graphics();
    if (currentValue !== undefined) {
      swatch.fillStyle(currentValue, 1);
      swatch.fillRoundedRect(swatchX, y, 14, 14, 2);
      swatch.lineStyle(1, 0xffffff, 0.3);
      swatch.strokeRoundedRect(swatchX, y, 14, 14, 2);
    }
    const displayStr = currentValue !== undefined ? hexStr(currentValue) : 'auto';
    const val = this.scene.add.text(swatchX + 18, y, displayStr, {
      fontSize: '10px', color: currentValue !== undefined ? '#cccccc' : '#555566', fontFamily: 'monospace',
    }).setInteractive({ useHandCursor: true });

    val.on('pointerdown', () => {
      const items = [
        { label: '(auto)', value: '__auto__', description: 'Use default from template/emotion' },
        ...presets.map(p => ({
          label: `■ ${p.label}`,
          value: p.value.toString(),
          description: hexStr(p.value),
        })),
        { label: '✏ Custom hex', value: '__custom__', description: 'Enter a hex color code' },
      ];
      showSelectionPopup(this.scene, label, items, (v) => {
        if (v === '__auto__') {
          onChange(undefined);
        } else if (v === '__custom__') {
          showTextInputDialog(this.scene, `${label} (hex)`, currentValue !== undefined ? hexStr(currentValue) : '', (hex) => {
            const clean = hex.replace('#', '').trim();
            const parsed = parseInt(clean, 16);
            if (!isNaN(parsed) && clean.length === 6) {
              onChange(parsed);
            }
          });
        } else {
          onChange(parseInt(v, 10));
        }
      });
    });

    this.contentContainer.add([lbl, swatch, val]);
    this.addHelpIcon(label, STEP_PANEL_W - PAD - 14, y);
    return y + ROW_H;
  }

  /** Read-only position display with Reset button */
  private addPosResetRow(
    label: string,
    pos: { x: number; y: number } | undefined,
    y: number,
    onReset: () => void,
  ): number {
    const lbl = this.scene.add.text(PAD, y, label, {
      fontSize: '10px', color: '#888888', fontFamily: 'monospace',
    });
    const displayStr = pos ? `${Math.round(pos.x)}, ${Math.round(pos.y)}` : 'auto';
    const val = this.scene.add.text(PAD + LABEL_W, y, displayStr, {
      fontSize: '10px', color: pos ? '#cccccc' : '#555566', fontFamily: 'monospace',
    });
    this.contentContainer.add([lbl, val]);
    this.addHelpIcon(label, STEP_PANEL_W - PAD - 14, y);

    if (pos) {
      const resetBtn = createSmallTechButton(this.scene, STEP_PANEL_W - PAD - 44, y - 2, 'Reset', TECH.DIM, onReset, 0.8);
      this.contentContainer.add(resetBtn);
    }
    return y + ROW_H;
  }

  private emitUpdate(): void {
    EventBus.emit(EditorEvents.STEP_UPDATED);
  }

  setVisible(visible: boolean): void { this.container.setVisible(visible); }
  destroy(): void { this.container.destroy(); }
}
